import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, CompactInlineAlertWidget, InlineAlertType, TextFieldWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { SessionServerSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import {
  ScimEnabledSiteDialogProps
} from '~src/settings/general-settings/components/sections/scim-settings/ScimEnabledSiteDialog';
import { useSelector } from '~src/settings/general-settings/utils';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { scimSettingsStyles } from './ScimSettingsStyles';



/**
 * Map of all sites with scim setting value
 */
interface ISiteScimEnabledMap {
  [site: string]: boolean;
}


interface SiteDataMap {
  id: string;
  name: string;
  isScimEnabled: boolean;
}
/**
 * Map of Site Name : Site Id
 */
interface ISiteIDMap {
  [site: string]: string;
}

export async function getAllSitesSettings(): Promise<SiteDataMap[]> {
  //TODO remove whole content of this function, make respected API and build data like siteData from response
  const siteData: SiteDataMap[] =[];
  return siteData;
}


export const SCIM_SERVER_BASE_URL = 'https://www.mytableauserver.com/sites/00000000-0000-0000-0000-000000000000/scim/v2/';

const generateScimBaseUrl = (): string => {
  return `${SCIM_SERVER_BASE_URL}`;
};

function getEditSiteSettingDescription(
  isEnabledForSiteMap: ISiteScimEnabledMap,
  isScimEnabledFutureSites: boolean): string {

  const sitesScimStatus = Object.values(isEnabledForSiteMap);
  const numberOfSitesEnabled = sitesScimStatus.filter(enabled => enabled).length;

  const editSitesDescription = isScimEnabledFutureSites ? Messages.scimEnabledFutureSites({ SITES_COUNT_LOC: L10n.localizedCount(numberOfSitesEnabled), TOTAL_COUNT: L10n.localizedCount(sitesScimStatus.length) })
    :Messages.scimGlobalToggleDialogSitesAllowed({ SITES_COUNT_LOC: L10n.localizedCount(numberOfSitesEnabled), TOTAL_COUNT: L10n.localizedCount(sitesScimStatus.length) });

  return editSitesDescription;
}


export function ScimServerSettingsSection(): JSX.Element {
  //base url & secret states.
  const [generating, setGenerating] = React.useState(false);
  const [secret, setSecret] = React.useState<string | null>(null);
  const [secretExists, setSecretExists] = React.useState(false);
  const generateSecretButtonHandler = useGenerateSecretButtonHandler(setSecret, setGenerating, setSecretExists, secretExists);
  const [scimProvisioningEnabled, setScimProvisioningEnabled] = React.useState(false);

  //other states
  const isEnabledForSiteMapInitial = React.useRef<ISiteScimEnabledMap>({});

  const [isEnabledForSiteMap, setIsEnabledForSiteMap] = React.useState<ISiteScimEnabledMap>({});
  const [siteNameToIDMap, setSiteNameToIDMap] = React.useState<ISiteIDMap>({});
  const [isScimEnabledFutureSites, setIsScimEnabledFutureSites] = React.useState<boolean>();

  const editSitesDescription = getEditSiteSettingDescription(
    isEnabledForSiteMap, isScimEnabledFutureSites
  );

  const shouldShowSettingsSection = true;

  // Fetch Scim site setting for all sites on server
  React.useEffect(() => {
    if (shouldShowSettingsSection) {
      let isMounted = true;
      getAllSitesSettings().then((allSiteSettings) => {
        if (isMounted) {
          let isEnabledForSiteMap: ISiteScimEnabledMap = {};
          let siteNameToIDMap: ISiteIDMap = {};
          for (let site of allSiteSettings) {
            isEnabledForSiteMap[site.name] = site.isScimEnabled;
            siteNameToIDMap[site.name] = site.id;
          }
          setIsEnabledForSiteMap(isEnabledForSiteMap);
          isEnabledForSiteMapInitial.current = isEnabledForSiteMap;
          setSiteNameToIDMap(siteNameToIDMap);
        }
      });
      return () => { isMounted = false; };
    }
  }, [shouldShowSettingsSection]);


  // TODO: Make API call to fetch existing SCIM configuration for the server and for which sites it exists
  // If configuration is found, set `scimProvisioningEnabled` to true
  // If a secret exists within the config, also set `secretExists` to true

  const saveData = React.useCallback(async (state)=>{
    const payload = {
      mode: 'serverLevel',
      isFutureSiteIncluded: state.isScimEnabledFutureSites,
      sideIds: {}
    };
    try{
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: Messages.siteSelectionSuccessful()
      });
    }catch(error){
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.siteSelectionError()
      });
    }

  },[]);

  const showEditSitesDialog = React.useCallback(() => {
    const modal = ReactModalService.open(ScimEnabledSiteDialogProps({
      scimEnabledStates: isEnabledForSiteMap,
      isScimEnabledFutureSites: isScimEnabledFutureSites,
      updateSites: (updatedEnabledForSite: ISiteScimEnabledMap) => {
        setIsEnabledForSiteMap(updatedEnabledForSite);

        // Only save the changed sites from original state
        // by filtering out the changed sites and then mapping to site id from name
        // since the API requires ids
        //TODO Use this logic to know which sites settings have been changed
        let pairs = _.pairs(updatedEnabledForSite);
        pairs = pairs.filter((pair: [string, boolean]) => {
          return isEnabledForSiteMapInitial.current[pair[0]] !== pair[1];
        });
        pairs = pairs.map((pair: [string, boolean]) => {
          return [siteNameToIDMap[pair[0]], pair[1]];
        });
        const mergedEnabledForSite =
          _.object(pairs);
      },
      updateScimEnabledFutureSiteFlag: (isEnabled: boolean) => {
        setIsScimEnabledFutureSites(isEnabled);
      },
      updateAll: async (_state) => {
        await saveData(_state);
      },
      close: () => { modal.close(); },
    }));
  }, [isEnabledForSiteMap, setIsEnabledForSiteMap, isScimEnabledFutureSites, isEnabledForSiteMapInitial, siteNameToIDMap, saveData]);


  if (!useShowSettingsSection()) {
    return null;
  }

  const toggleScimProvisioning = (checked: boolean) => {

    // base url and secret button will only be shown if scim is enabled.
    if (checked) {
      setScimProvisioningEnabled(true);
      // TODO: Make API call to save scim configuration for server.
    }

    if (!checked) {
      setScimProvisioningEnabled(false);
      setSecret(null);
      if (secretExists)
        deleteScimApiToken(setSecret, setSecretExists);
      // TODO: Make API call to disable scim configuration on server and delete secret (if exists)
    }
  };

  return <SettingsSection title={Messages.scimSettingsHeading()} description={renderDescription()}>
    <CheckBoxWithLabelWidget
      label={Messages.scimSettingsEnable()}
      checked={scimProvisioningEnabled}
      testId={'scim-settings-checkbox'}
      handleChange={toggleScimProvisioning}
    />
    {scimProvisioningEnabled && (
      <div style={scimSettingsStyles.baseUrlContainer}>
        <TextFieldWidget
          label={Messages.scimBaseUrl()}
          testId='scim-settings-baseUrl'
          style={scimSettingsStyles.baseUrlField}
          value={generateScimBaseUrl()}
          onClick={selectAll}
          readOnly
        />
        <div>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId={'scim-settings-generate-secret-button'}
            className={scimSettingsStyles.secretButton}
            type={'button'}
            disabled={generating}
            onClick={generateSecretButtonHandler}
          >
            {Messages.scimGenerateSecretButton()}
          </ButtonWidget>
          {/* secret will be shown only once */}
          {secret && (
            <div className={scimSettingsStyles.secretContainer}>
              <TextFieldWidget
                kind='line'
                label={Messages.scimSettingsSecret()}
                testId={'scim-settings-secret'}
                value={secret}
                style={scimSettingsStyles.secretField}
                onClick={selectAll}
                readOnly
              />
              <CompactInlineAlertWidget type={InlineAlertType.Info} testId="scim-settings-secret-info" style={scimSettingsStyles.secretInfo}>
                {<div>{Messages.scimSecretInfo()}</div>}
              </CompactInlineAlertWidget>
            </div>
          )}
        </div>
        <p>{editSitesDescription}
          <b>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <TextLinkWidget onClick={showEditSitesDialog} href='javascript:void(0)' testId={'scim-edit-sites-dialog'}>
              {Messages.edit()}
            </TextLinkWidget>
          </b>
        </p>
      </div>
    )}
  </SettingsSection>;
}

export const ScimServerSettingsSpecs = componentRegistryEntry({
  name: 'ScimServerSettingsSection',
  component: ScimServerSettingsSection,
});


function selectAll(event: any): void {
  event.target.select();
}

function useGenerateSecretButtonHandler(
  setSecret: (secret: string | null) => void,
  setGenerating: (loading: boolean) => void,
  setSecretExists: (exist: boolean) => void,
  secretExists: boolean | null
) {
  return React.useCallback(() => {
    const generateSecret = async () => {
      setGenerating(true);
      try {
        if (secretExists) {
          await deleteScimApiToken(setSecret, setSecretExists);
        }
        await createScimApiToken(setSecret, setSecretExists);
      } catch (error) {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.scimSettingsGenerateSecretError()
        });
      } finally {
        setGenerating(false);
      }
    };

    if (secretExists) {
      openReactConfirmationDialog<void, any>({
        title: Messages.scimGenerateSecretDialogTitle(),
        message: Messages.scimGenerateSecretDialogMessage(),
        paramsForAction: undefined,
        action: generateSecret,
        actionName: Messages.scimGenerateSecretButton(),
        isActionDelete: false,
        dialogWidth: TabDialogWidth.width375px
      });
    } else {
      generateSecret();
    }
  }, [secretExists, setSecret, setGenerating, setSecretExists]);
}

function createScimApiToken(setSecret: (token: string | null) => void, setSecretExists: (exist: boolean) => void): Promise<any> {

  // TODO: Make API call to generate scim secret
  ToasterService.instance.pop({
    type: ToastType.Success,
    text: Messages.scimSettingsGenerateSecretSuccess()
  });
  setSecretExists(true);
  return Promise.resolve();
}

function deleteScimApiToken(setSecret: (token: string | null) => void, setSecretExists: (exist: boolean) => void): Promise<any> {

  //TODO: Make API call to delete scim secret
  setSecret(null);
  setSecretExists(false);
  return Promise.resolve();
}

function useShowSettingsSection(): boolean {
  const isServerAdmin = useSelector(SessionUserSelectors.isSessionServerAdmin);
  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
  return FeatureFlags.scimServerSettingsEnabled && (isServerAdmin && !isOnlineEnvironment);

}

function renderDescription() {
  return (
    <>
      <span>{Messages.scimSettingsDescription()}</span>
      <TextLinkWidget target={'_blank'}
        testId={'scim-settings-learn-more'}
        className={learnMoreStyle}
        href={`https://help.tableau.com/current/online/en-us/scim_config_online.htm`}>
        {Messages.learnMore()}
      </TextLinkWidget>
    </>
  );
}